import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import QMSLayout from "../components/QMSLayout";
import { useAuth } from "../App";
import { Shield, Play, RefreshCw, CheckCircle2, XCircle, AlertTriangle, Clock, ChevronDown, ChevronRight, Loader2, FileText, Lock, Eye, Globe, Download, Zap } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const WORKER_META = {
  functional: { icon: Zap, color: "#38BDF8", label: "Functional" },
  regression: { icon: RefreshCw, color: "#A78BFA", label: "Regression" },
  security: { icon: Lock, color: "#F87171", label: "Security" },
  ai_workflow: { icon: Zap, color: "#FBBF24", label: "AI Workflow" },
  ui_responsive: { icon: Eye, color: "#4ADE80", label: "UI/Responsive" },
  i18n: { icon: Globe, color: "#36A9E1", label: "i18n" },
  export: { icon: Download, color: "#F472B6", label: "Export" },
  post_regression: { icon: RefreshCw, color: "#FB923C", label: "Post-Regression" },
};

const DECISION_STYLES = {
  GO: { bg: "bg-[#4ADE80]/10", text: "text-[#4ADE80]", border: "border-[#4ADE80]" },
  "CONDITIONAL GO": { bg: "bg-[#FBBF24]/10", text: "text-[#FBBF24]", border: "border-[#FBBF24]" },
  "NO-GO": { bg: "bg-[#F87171]/10", text: "text-[#F87171]", border: "border-[#F87171]" },
};

function WorkerCard({ report }) {
  const [expanded, setExpanded] = useState(false);
  const meta = WORKER_META[report.worker_name] || { icon: FileText, color: "#8B95A5", label: report.worker_name };
  const Icon = meta.icon;
  const allPassed = report.failed === 0 && report.tests_run > 0;

  return (
    <div className="qms-card" data-testid={`worker-${report.worker_name}`}>
      <div className="qms-card-body">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
          <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: `${meta.color}15` }}>
            <Icon size={18} style={{ color: meta.color }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-white">{meta.label}</span>
              {allPassed ? (
                <CheckCircle2 size={14} className="text-[#4ADE80]" />
              ) : report.failed > 0 ? (
                <XCircle size={14} className="text-[#F87171]" />
              ) : (
                <Clock size={14} className="text-[#FBBF24]" />
              )}
            </div>
            <p className="text-[11px] text-[#8B95A5]">{report.passed}/{report.tests_run} passed, {report.failed} failed</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] px-2 py-0.5 rounded ${allPassed ? "bg-[#4ADE80]/10 text-[#4ADE80]" : "bg-[#F87171]/10 text-[#F87171]"}`}>
              {report.regression_status}
            </span>
            {expanded ? <ChevronDown size={14} className="text-[#8B95A5]" /> : <ChevronRight size={14} className="text-[#8B95A5]" />}
          </div>
        </div>
        {expanded && (
          <div className="mt-3 pt-3 border-t border-[rgba(255,255,255,0.05)] space-y-2">
            <p className="text-xs text-[#8B95A5]">{report.summary}</p>
            {report.duration_seconds > 0 && (
              <p className="text-[10px] text-[#555]">Duration: {report.duration_seconds.toFixed(1)}s</p>
            )}
            {report.issues && report.issues.length > 0 && (
              <div className="space-y-1.5 mt-2">
                <p className="text-xs text-[#F87171] font-medium">Issues ({report.issues.length}):</p>
                {report.issues.map((iss, idx) => (
                  <div key={idx} className="p-2 rounded bg-[#0D0F14] border border-[rgba(255,255,255,0.05)]">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${iss.severity === "critical" ? "bg-[#F87171]/20 text-[#F87171]" : iss.severity === "high" ? "bg-[#FBBF24]/20 text-[#FBBF24]" : "bg-[#8B95A5]/20 text-[#8B95A5]"}`}>
                        {iss.severity.toUpperCase()}
                      </span>
                      <span className="text-xs text-white">{iss.title}</span>
                    </div>
                    <p className="text-[10px] text-[#8B95A5] mt-1">{iss.description}</p>
                    {iss.recommendation && <p className="text-[10px] text-[#36A9E1] mt-1">{iss.recommendation}</p>}
                  </div>
                ))}
              </div>
            )}
            {report.evidence_artifacts && report.evidence_artifacts.length > 0 && (
              <details className="mt-2">
                <summary className="text-[10px] text-[#555] cursor-pointer">Evidence ({report.evidence_artifacts.length})</summary>
                <div className="mt-1 p-2 rounded bg-[#0D0F14] max-h-32 overflow-y-auto">
                  {report.evidence_artifacts.map((e, i) => (
                    <p key={i} className="text-[10px] text-[#8B95A5] font-mono">{e}</p>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function QASupervisorPage() {
  const { t } = useTranslation("common");
  const { user } = useAuth();
  const [runs, setRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [milestone, setMilestone] = useState("");

  const fetchRuns = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/qa/runs`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setRuns(data.runs || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  const startRun = async () => {
    if (!milestone.trim()) return;
    setRunning(true);
    try {
      const res = await fetch(`${API}/api/qa/runs/sync`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ milestone: milestone.trim() }),
      });
      if (res.ok) {
        const report = await res.json();
        setSelectedRun({ run_id: report.report_id, report, status: "completed", milestone: milestone.trim() });
        setMilestone("");
        fetchRuns();
      }
    } catch (e) { console.error(e); }
    setRunning(false);
  };

  const viewRun = async (runId) => {
    try {
      const res = await fetch(`${API}/api/qa/runs/${runId}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setSelectedRun(data);
      }
    } catch (e) { console.error(e); }
  };

  const report = selectedRun?.report;
  const decision = report?.final_recommendation;
  const decStyle = DECISION_STYLES[decision] || DECISION_STYLES["NO-GO"];

  return (
    <QMSLayout title="QA Supervisor">
      <div className="space-y-5" data-testid="qa-supervisor-page">
        {/* Run Controls */}
        <div className="qms-card">
          <div className="qms-card-body">
            <div className="flex flex-col md:flex-row gap-3 items-end">
              <div className="flex-1">
                <label className="block text-[#8B95A5] text-xs mb-1">Milestone</label>
                <input value={milestone} onChange={e => setMilestone(e.target.value)} placeholder="e.g. M7C-P1, M6.2.2, Sprint 14"
                  className="qms-input w-full" data-testid="qa-milestone-input" />
              </div>
              <button onClick={startRun} disabled={!milestone.trim() || running}
                className="btn-primary flex items-center gap-2 whitespace-nowrap" data-testid="qa-start-run">
                {running ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
                {running ? "Running QA..." : "Start QA Run"}
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Runs List */}
          <div className="lg:col-span-1 space-y-3">
            <h3 className="text-sm font-semibold text-white">QA Runs</h3>
            {loading && <p className="text-xs text-[#8B95A5]">Loading...</p>}
            {runs.map(run => (
              <div key={run.run_id} onClick={() => viewRun(run.run_id)}
                className={`qms-card cursor-pointer transition-colors hover:border-[#36A9E1]/30 ${selectedRun?.run_id === run.run_id ? "border-[#36A9E1]/50" : ""}`}
                data-testid={`qa-run-${run.run_id}`}>
                <div className="qms-card-body py-2.5 px-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-white">{run.milestone}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${run.status === "completed" ? "bg-[#4ADE80]/10 text-[#4ADE80]" : run.status === "running" ? "bg-[#FBBF24]/10 text-[#FBBF24]" : "bg-[#8B95A5]/10 text-[#8B95A5]"}`}>
                      {run.status}
                    </span>
                  </div>
                  <p className="text-[10px] text-[#555] font-mono">{run.run_id}</p>
                  {run.started_at && <p className="text-[10px] text-[#555]">{new Date(run.started_at).toLocaleString()}</p>}
                </div>
              </div>
            ))}
            {!loading && runs.length === 0 && <p className="text-xs text-[#8B95A5]">No QA runs yet</p>}
          </div>

          {/* Report Detail */}
          <div className="lg:col-span-2 space-y-4">
            {!report && <div className="text-center py-16 text-[#8B95A5]"><Shield size={48} className="mx-auto mb-4 opacity-20" /><p>Select or start a QA run</p></div>}
            {report && (
              <>
                {/* Decision Banner */}
                <div className={`p-4 rounded border ${decStyle.bg} ${decStyle.border}`} data-testid="qa-decision">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-lg font-bold font-['Syne'] ${decStyle.text}`}>{decision}</p>
                      <p className="text-xs text-[#8B95A5] mt-1">{report.milestone} | {report.report_id}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-white">{report.total_passed}/{report.total_tests} passed</p>
                      <p className="text-[10px] text-[#8B95A5]">{report.total_failed} failed</p>
                    </div>
                  </div>
                </div>

                {/* Severity Summary */}
                {report.severity_summary && (
                  <div className="grid grid-cols-5 gap-2" data-testid="severity-summary">
                    {[
                      { key: "critical", label: "Critical", color: "#F87171" },
                      { key: "high", label: "High", color: "#FBBF24" },
                      { key: "medium", label: "Medium", color: "#FB923C" },
                      { key: "low", label: "Low", color: "#36A9E1" },
                      { key: "info", label: "Info", color: "#8B95A5" },
                    ].map(s => (
                      <div key={s.key} className="text-center p-2 rounded bg-[#1F222D] border border-[rgba(255,255,255,0.05)]">
                        <p className="text-lg font-bold" style={{ color: s.color }}>{report.severity_summary[s.key] || 0}</p>
                        <p className="text-[10px] text-[#8B95A5]">{s.label}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Blockers */}
                {report.blocker_list && report.blocker_list.length > 0 && (
                  <div className="p-3 rounded bg-[#F87171]/5 border border-[#F87171]/20" data-testid="blockers">
                    <p className="text-xs font-bold text-[#F87171] mb-2">BLOCKERS ({report.blocker_list.length})</p>
                    {report.blocker_list.map((b, i) => (
                      <div key={i} className="flex items-center gap-2 py-1">
                        <XCircle size={12} className="text-[#F87171] shrink-0" />
                        <span className="text-xs text-white">{b.area}: {b.title}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Worker Reports */}
                <div className="space-y-2" data-testid="worker-reports">
                  <h3 className="text-sm font-semibold text-white">Worker Reports ({report.workers_run?.length || 0})</h3>
                  {(report.worker_reports || []).map((wr, i) => (
                    <WorkerCard key={i} report={wr} />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </QMSLayout>
  );
}
